/**
 * Notes modal for one photo-list car: the five-circle progress row up top
 * (sharing the tiles' optimistic state via props — the table owns it), then
 * the full note history reading like a paper log — oldest at the top, newest
 * at the bottom, each entry timestamped and attributed — with the write box
 * underneath, chat-style, and the view auto-scrolled to the newest on open.
 * Add-only — no edit, no delete — matching the backend's vehicle_notes
 * contract. The table is shared with the audit worklist but each page shows
 * only its own thread (the backend filters by source).
 *
 * Modal chrome copies components/photos/AddVehicleModal.tsx (backdropStyle /
 * dialogStyle, ~line 299) — there is no shared Dialog component yet in INVI.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"

import {
  VehicleNoteEntry,
  addVehicleNote,
  listVehicleNotes,
  type PhotoListVehicle,
  type ProgressStatus,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import { formatTimestamp } from "../../utils/format"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"
import ProgressCircles from "./ProgressCircles"

/** Day-scale relative age — notes live for months, so the hour-scale
 * formatAge helper is the wrong ruler here. */
function formatNoteAge(createdAt: string): string {
  const ageDays = Math.floor(
    (Date.now() - parseUtcTimestamp(createdAt).getTime()) / 86_400_000,
  )
  if (ageDays <= 0) return "today"
  if (ageDays === 1) return "1d ago"
  if (ageDays < 60) return `${ageDays}d ago`
  if (ageDays < 365) return `${Math.floor(ageDays / 30)}mo ago`
  const years = Math.floor(ageDays / 365)
  return years === 1 ? "1y ago" : `${years}y ago`
}

/** A note belongs to the car's current stay when it is unstamped (pre-stamp
 * history, unknowable) or its stamped stock matches the row's stock. Stock is
 * per-acquisition, so a mismatch means a previous stay. */
function isCurrentStay(note: VehicleNoteEntry, currentStock: string | null): boolean {
  if (!note.stock || !currentStock) return true
  return note.stock.trim().toUpperCase() === currentStock.trim().toUpperCase()
}

interface Props {
  vehicle: PhotoListVehicle
  dealerGroupId: number | null
  /** The car's five-circle state, already override-merged by PhotoListTable —
   * a tap here goes through the same optimistic handler as the tiles, so the
   * modal and the card behind it always agree. */
  progress: ProgressStatus | null
  onProgressTap: (status: ProgressStatus) => void
  onClose: () => void
  /** Fired after a note saves, with the new latest note's body — lets the
   * table repaint its Notes cell without refetching the sheet. */
  onNoteAdded: (vin: string, body: string) => void
  /** Present only for a car added by hand to today's list (Ian, 2026-10-11):
   * shows Remove, behind a confirm step. The modal closes once it resolves. */
  onRemove?: () => Promise<void>
}

export default function VehicleNotesModal({
  vehicle,
  dealerGroupId,
  progress,
  onProgressTap,
  onClose,
  onNoteAdded,
  onRemove,
}: Props) {
  const [notes, setNotes] = useState<VehicleNoteEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Previous stays fold away by default — their headers alone tell the story
  // ("Earlier — under stock 2677 (1 note)"); clicking a header toggles it.
  const [expandedStocks, setExpandedStocks] = useState<Set<string>>(() => new Set())
  const dialogRef = useRef<HTMLDivElement>(null)
  // Remove asks once inside the modal (no browser confirm pop-up).
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  const handleRemove = async () => {
    if (!onRemove) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await onRemove()
      onClose()
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : "Could not remove the car.")
      setRemoving(false)
    }
  }

  const toggleStock = useCallback((stock: string) => {
    setExpandedStocks((prev) => {
      const next = new Set(prev)
      if (next.has(stock)) next.delete(stock)
      else next.add(stock)
      return next
    })
  }, [])

  const vin = vehicle.vin ?? ""

  // Newest lives at the bottom (Ian, 2026-08-26) — keep it in view when the
  // history loads and when a fresh note lands.
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog) dialog.scrollTop = dialog.scrollHeight
  }, [loading, notes.length])

  // Load the full history when the modal opens.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const loaded = await listVehicleNotes(vin, dealerGroupId)
        if (!cancelled) setNotes(loaded)
      } catch {
        if (!cancelled) setLoadError("Failed to load notes")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [vin, dealerGroupId])

  // Escape closes, matching every INVI popover/modal.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  const handleAdd = useCallback(async () => {
    const text = draft.trim()
    if (!text || saving) return
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await addVehicleNote(vin, text, vehicle.stock, dealerGroupId)
      setNotes((prev) => [saved, ...prev])
      setDraft("")
      onNoteAdded(vin, saved.body)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save note"
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }, [draft, saving, vin, vehicle.stock, dealerGroupId, onNoteAdded])

  const title =
    [vehicle.year != null ? String(vehicle.year) : null, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(" ") || "Vehicle"

  // The log reads top-down like paper: oldest first, newest at the bottom
  // (Ian, 2026-08-26). The API returns newest-first (the Notes cell needs
  // that), so display order is the reverse. Previous-stock groups come first
  // — they are the car's older stays — then the current stay's notes.
  const chronological = [...notes].reverse()
  const currentNotes = chronological.filter((note) => isCurrentStay(note, vehicle.stock))
  const previousStockGroups: Array<[string, VehicleNoteEntry[]]> = []
  for (const note of chronological) {
    if (isCurrentStay(note, vehicle.stock)) continue
    const stock = note.stock as string
    const group = previousStockGroups.find(([s]) => s === stock)
    if (group) group[1].push(note)
    else previousStockGroups.push([stock, [note]])
  }

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div ref={dialogRef} style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <h2 style={titleStyle}>Notes — {title}</h2>
            {vin && <div style={vinLineStyle}>VIN {vin}</div>}
          </div>
          <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close">
            ×
          </button>
        </div>

        {/* The same five-circle row as the tiles (Ian, 2026-09-10) — on
            desktop this is the only place to set a car's progress. VIN-less
            rows can't key a state, matching the tiles. */}
        {vin && (
          <div style={{ marginBottom: THEME.spacing.sm }}>
            <ProgressCircles active={progress} onTap={onProgressTap} />
          </div>
        )}

        {/* History reads top-down: previous stays first (each folded under a
            "previous stock" divider — stock changes per acquisition), then
            the current stay, newest note at the bottom. */}
        {loading && <div style={mutedTextStyle}>Loading notes…</div>}
        {loadError && !loading && <div style={errorTextStyle}>{loadError}</div>}
        {!loading && !loadError && notes.length === 0 && (
          <div style={mutedTextStyle}>No notes on this car yet.</div>
        )}
        {!loading &&
          previousStockGroups.map(([stock, groupNotes]) => {
            const expanded = expandedStocks.has(stock)
            return (
              <React.Fragment key={stock}>
                <button
                  type="button"
                  onClick={() => toggleStock(stock)}
                  style={stintDividerButtonStyle}
                >
                  {expanded ? (
                    <ChevronDown size={13} strokeWidth={2} />
                  ) : (
                    <ChevronRight size={13} strokeWidth={2} />
                  )}
                  Earlier — under stock {stock} ({groupNotes.length}{" "}
                  note{groupNotes.length !== 1 ? "s" : ""})
                </button>
                {expanded &&
                  groupNotes.map((note) => <NoteEntry key={note.id} note={note} />)}
              </React.Fragment>
            )
          })}
        {!loading && previousStockGroups.length > 0 && currentNotes.length > 0 && (
          <div style={stintDividerStyle}>
            Current stay{vehicle.stock ? ` — stock ${vehicle.stock}` : ""}
          </div>
        )}
        {!loading && currentNotes.map((note) => <NoteEntry key={note.id} note={note} />)}

        {/* Write box at the bottom, chat-style — a new note lands right where
            it was typed, keeping the newest-at-the-bottom order true. */}
        {/* Disabled until the history loads: a note saved before the initial
            GET resolves would be wiped from view when the older snapshot
            lands (the note saves fine server-side — the vanishing invites a
            duplicate re-write). */}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a note… (notes are permanent — a wrong one is corrected by writing another)"
          rows={3}
          disabled={saving || loading}
          style={{ ...textareaStyle, marginTop: THEME.spacing.sm }}
        />
        <div style={addRowStyle}>
          {saveError && <span style={errorTextStyle}>{saveError}</span>}
          <button
            type="button"
            onClick={handleAdd}
            disabled={!draft.trim() || saving || loading}
            style={addButtonStyle(!draft.trim() || saving || loading)}
          >
            {saving ? "Saving…" : "Add Note"}
          </button>
        </div>

        {/* Kept at the very bottom, away from the circles and the note box,
            so a stray tap can't take a car off the list. */}
        {onRemove && (
          <div style={removeSectionStyle}>
            {confirmingRemove ? (
              <>
                <span style={mutedTextStyle}>
                  Remove this car from today&apos;s list? Its notes and circle stay.
                </span>
                <div style={removeButtonsStyle}>
                  <button
                    type="button"
                    onClick={() => setConfirmingRemove(false)}
                    disabled={removing}
                    style={keepButtonStyle}
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    onClick={handleRemove}
                    disabled={removing}
                    style={removeButtonStyle}
                  >
                    {removing ? "Removing…" : "Remove"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <span style={mutedTextStyle}>Added by hand to today&apos;s list.</span>
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(true)}
                  style={removeButtonStyle}
                >
                  Remove from list
                </button>
              </>
            )}
            {removeError && <span style={errorTextStyle}>{removeError}</span>}
          </div>
        )}
      </div>
    </div>
  )
}

function NoteEntry({ note }: { note: VehicleNoteEntry }) {
  return (
    <div style={noteEntryStyle}>
      <div style={noteMetaStyle}>
        {formatTimestamp(note.created_at)} · {formatNoteAge(note.created_at)}
        {note.author_name ? ` · ${note.author_name}` : ""}
      </div>
      <div style={noteBodyStyle}>{note.body}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles — chrome matches AddVehicleModal.tsx; width adapted so the dialog
// fits a 390px phone instead of forcing minWidth 520.
// ---------------------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const dialogStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.lg,
  padding: THEME.spacing.lg,
  width: "min(560px, calc(100vw - 32px))",
  maxHeight: "85vh",
  overflowY: "auto",
  fontFamily: THEME.fonts.primary,
  boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const vinLineStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: THEME.fonts.mono,
  marginTop: 2,
}

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 24,
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1,
}

const textareaStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: THEME.spacing.sm,
  fontSize: 13,
  fontFamily: THEME.fonts.primary,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  resize: "vertical",
}

const addRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
  margin: `${THEME.spacing.sm} 0 ${THEME.spacing.md}`,
}

const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  fontFamily: THEME.fonts.primary,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
})

const removeSectionStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: THEME.spacing.sm,
  borderTop: `1px solid ${THEME.colors.silver}`,
  paddingTop: THEME.spacing.sm,
}

const removeButtonsStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
}

// Outline in the danger color — the destructive twin of addButtonStyle's
// shape (padding, size, radius) without its navy fill.
const removeButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.semantic.danger,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}

const keepButtonStyle: React.CSSProperties = {
  ...removeButtonStyle,
  border: `1px solid ${THEME.colors.silver}`,
  color: THEME.colors.neutral.light.textPrimary,
}

const mutedTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

const errorTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.semantic.danger,
}

const noteEntryStyle: React.CSSProperties = {
  borderTop: `1px solid ${THEME.colors.silver}`,
  padding: `${THEME.spacing.sm} 0`,
}

const stintDividerStyle: React.CSSProperties = {
  marginTop: THEME.spacing.sm,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  background: THEME.colors.neutral.light.background,
  borderRadius: THEME.borderRadius.sm,
}

// The collapsible variant for previous stays: same look as the divider, but a
// real <button> (keyboard for free) spanning the row, chevron leading.
const stintDividerButtonStyle: React.CSSProperties = {
  ...stintDividerStyle,
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  textAlign: "left",
  border: "none",
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const noteMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: 2,
}

const noteBodyStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  whiteSpace: "pre-wrap",
}
